import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import Navbar from "../Component/LandingV2/Navbar";
import Footer from "../Component/LandingV2/Footer";
import { UserInfo } from "../context/AuthContext";
import hashSHA256 from "../utils/hashSHA256";
import "./RegisterPage.scss";

export interface RegisterPageProps {
  setUserInfo: (userInfo: UserInfo) => void;
}

const RegisterPage = (props: RegisterPageProps) => {
  const [loadingState, setLoadingState] = useState(false);
  const [messageState, setMessageState] = useState<string | null>(null);
  const [errorState, setErrorState] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoadingState(true);
    setErrorState(null);
    setMessageState(null);

    try {
      const eTarget = e.target as typeof e.target & {
        email: { value: string };
        password: { value: string };
        confirmPassword: { value: string };
      };

      const [username, email, password, confirmPassword] = [
        eTarget.email.value.split("@")[0],
        eTarget.email.value,
        eTarget.password.value,
        eTarget.confirmPassword.value,
      ];

      if (password !== confirmPassword) {
        setErrorState("Passwords do not match.");
        return;
      }

      const res = await fetch(`${process.env.REACT_APP_AUTH_URL || "http://localhost:3000"}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          email,
          passwordHash: await hashSHA256(password),
        }),
      });

      const resData = await res.json();
      if (res.ok) {
        setMessageState("User registered successfully 🎉 Please verify your email.");
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        throw new Error(resData?.message || "Registration failed.");
      }
    } catch (err: any) {
      setErrorState(err?.message || "Could not register user");
    } finally {
      setLoadingState(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="register-page">
        <div className="modal-backdrop"></div>
        <div className="register-background-image">
          <img src="/assets/images/login/background_1108.png" alt="Background" />
        </div>
        <div className="register-form-card">
          <h2 className="register-title">Create Account</h2>
          <form onSubmit={onSubmit} className="register-form">
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              name="email"
              type="email"
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((s) => !s)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"} 
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword((s) => !s)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />} 
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />



            <FormControlLabel
              control={<Checkbox name="terms" required />}
              label="I agree to the Terms of Service and Privacy Policy"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loadingState}
              startIcon={loadingState ? <CircularProgress size={20} /> : null}
            >
              Create Account
            </Button>
          </form>

          {errorState && <Typography color="error" mt={2}>{errorState}</Typography>}
          {messageState && <Typography color="success.main" mt={2}>{messageState}</Typography>}

          <Box mt={2}>
            Already have an account? <Link to="/login">Log In</Link>
          </Box>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RegisterPage;
